import { useEffect, useState } from "react";
import { MenuItem, MenuItems } from "../../../../../common/AppSidebar/types";
import { FileItem } from "../FileItem";
import styles from "./styles.module.css";

interface Props {
  files: MenuItems;
  multiple: boolean;
  onFileClick: (hash: string, fileName: string) => void;
  onSelectionChange?: (
    selected: Array<{ hash: string; filename: string }>,
  ) => void;
}

const getFileHash = (item: MenuItem): string | undefined => {
  return item.value?.userfiles?.hash;
};

const isFileItem = (item: MenuItem): boolean => {
  return !item.children?.length && !!getFileHash(item);
};

export const FileList = ({
  files,
  multiple,
  onFileClick,
  onSelectionChange,
}: Props) => {
  const [openFolderId, setOpenFolderId] = useState<string | null>(null);
  const [selected, setSelected] = useState<
    Record<string, { hash: string; filename: string }>
  >({});

  useEffect(() => {
    if (multiple && onSelectionChange) {
      onSelectionChange(Object.values(selected));
    }
  }, [selected, multiple, onSelectionChange]);

  const handleFolderClick = (folder: MenuItem) => {
    setOpenFolderId(openFolderId === folder.id ? null : folder.id);
  };

  const handleCheckboxChange = (
    hash: string,
    filename: string,
    checked: boolean,
  ) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (checked) {
        next[hash] = { hash, filename };
      } else {
        delete next[hash];
      }
      return next;
    });
  };

  const renderFileItem = (item: MenuItem) => {
    const hash = getFileHash(item);
    const isFile = isFileItem(item);

    if (isFile && multiple) {
      return (
        <FileItem
          key={item.id}
          item={item}
          fileType="checkbox"
          isChecked={!!selected[hash!]}
          onFileClick={(h, n, checked) =>
            handleCheckboxChange(h, n, checked ?? false)
          }
          onFolderClick={handleFolderClick}
        />
      );
    } else {
      return (
        <FileItem
          key={item.id}
          item={item}
          fileType="button"
          onFileClick={onFileClick}
          onFolderClick={handleFolderClick}
        />
      );
    }
  };

  return (
    <div className={styles["file-list"]}>
      {files.map((item) => (
        <div key={item.id} className={styles["file-item-wrapper"]}>
          {renderFileItem(item)}
          {item.children?.length && openFolderId === item.id && (
            <div className={styles["file-list-children"]}>
              {item.children.map(renderFileItem)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
